import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import type { CreateEmployee, Employee, UpdateEmployee } from '@dcc-erp/shared';

import { api, apiJson } from '../api';

const key = (companyId: string | undefined) => ['employees', companyId] as const;

export function useEmployees(companyId: string | undefined, activeOnly = true) {
  return useQuery({
    queryKey: [...key(companyId), { activeOnly }],
    queryFn: () => api<Employee[]>(`/companies/${companyId}/employees?activeOnly=${activeOnly}`),
    enabled: !!companyId,
  });
}

export function useEmployee(companyId: string | undefined, id: string | undefined) {
  return useQuery({
    queryKey: ['employees', companyId, id],
    queryFn: () => api<Employee>(`/companies/${companyId}/employees/${id}`),
    enabled: !!companyId && !!id,
  });
}

export function useCreateEmployee(companyId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: Omit<CreateEmployee, 'companyId'>) =>
      apiJson<typeof input, Employee>('POST', `/companies/${companyId}/employees`, input),
    onSuccess: () => qc.invalidateQueries({ queryKey: key(companyId) }),
  });
}

export function useUpdateEmployee(companyId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateEmployee }) =>
      apiJson<UpdateEmployee, Employee>('PATCH', `/companies/${companyId}/employees/${id}`, data),
    onSuccess: () => qc.invalidateQueries({ queryKey: key(companyId) }),
  });
}

export function useDeleteEmployee(companyId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiJson<undefined, Employee>('DELETE', `/companies/${companyId}/employees/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: key(companyId) }),
  });
}
